// I numeri del foglio «KPI SALES INDIVIDUALE» (SEZ-2, SEZ-16; piano M7, D2; nota del mock-up del 17/10/2026, «Che cosa
// dicono i fogli» e scelte di Claude 1, 7, 8, 9). Il server manda soltanto conteggi grezzi (`ReportMetrics`); qui ci
// sono le formule, una sola volta per sommario, tabelle, schede e obiettivi: percentuali, minuti, valore medio, frecce
// del confronto e colori degli obiettivi. Le percentuali si calcolano sempre sui conteggi sommati, mai come media delle
// persone. Il modulo non importa niente, così l'interfaccia lo può usare senza portarsi dietro il resto del dominio.
// L'oggetto vuoto è scritto per intero: il tipo vuole ogni chiave, e l'elenco delle chiavi viene da qui, nello stesso ordine.
const EMPTY_METRICS = Object.freeze({
    calls: 0,
    answered: 0,
    leads: 0,
    answeredLeads: 0,
    closedEarly: 0,
    manualCalls: 0,
    callSeconds: 0,
    discoBooked: 0,
    discoDue: 0,
    discoHeld: 0,
    discoRescheduled: 0,
    discoMinutes: 0,
    demoBooked: 0,
    demoDue: 0,
    demoHeld: 0,
    demoRescheduled: 0,
    demoMinutes: 0,
    discoFromClose: 0,
    demoFromClose: 0,
    closings: 0,
    valueCents: 0,
});
/** Le chiavi di `ReportMetrics`, nell'ordine del tipo (i contratti le ripetono con un test di parità). */
export const REPORT_METRIC_KEYS = Object.freeze(Object.keys(EMPTY_METRICS));
/** I Lead diversi si contano sull'unione: due righe non si sommano mai. */
const DISTINCT_METRICS = new Set(["leads", "answeredLeads"]);
/** Tutte tranne `leads` e `answeredLeads`, che non si sommano. */
export const ADDITIVE_REPORT_METRICS = Object.freeze(REPORT_METRIC_KEYS.filter((key) => !DISTINCT_METRICS.has(key)));
/** I conteggi di un periodo senza niente: un oggetto nuovo a ogni chiamata. */
export function emptyReportMetrics() {
    return { ...EMPTY_METRICS };
}
/**
 * Somma in `into` i conteggi additivi di `part` e restituisce `into`. `leads` e `answeredLeads` di `part` si ignorano:
 * i Lead diversi di più righe li conta il database sull'unione. Un conteggio che non è un numero finito è un
 * `TypeError`: il testo di un `count` del database non si concatena in silenzio.
 */
export function addReportMetrics(into, part) {
    for (const key of ADDITIVE_REPORT_METRICS) {
        const value = part[key];
        if (value === undefined)
            continue;
        if (typeof value !== "number" || !Number.isFinite(value))
            throw new TypeError(`${key} must be a finite number`);
        into[key] += value;
    }
    return into;
}
/** I quattro gruppi del foglio con i loro numeri, nell'ordine di `KPI_GROUPS` del mock-up. */
export const REPORT_KPI_GROUPS = Object.freeze([
    Object.freeze({ id: "calls", kpis: Object.freeze(["calls", "answered", "answerRate", "leads", "leadRate", "minutes"]) }),
    Object.freeze({ id: "disco", kpis: Object.freeze(["discoBooked", "discoOnCalls", "discoOnAnswered", "discoDue", "discoHeld", "discoShow", "discoRescheduled"]) }),
    Object.freeze({ id: "demo", kpis: Object.freeze(["demoBooked", "demoOnDisco", "demoDue", "demoHeld", "demoRescheduled", "demoShow"]) }),
    Object.freeze({ id: "closings", kpis: Object.freeze(["closings", "closeRate", "value", "averageValue"]) }),
]);
/** «Sintesi», le colonne di partenza delle tabelle (`SINTESI` del mock-up). */
export const REPORT_SUMMARY_KPIS = Object.freeze(["calls", "answerRate", "leads", "discoBooked", "discoOnCalls", "demoHeld", "closings", "value"]);
export const REPORT_KPI_UNITS = Object.freeze({
    calls: "count",
    answered: "count",
    answerRate: "percent",
    leads: "count",
    leadRate: "percent",
    minutes: "minutes",
    discoBooked: "count",
    discoOnCalls: "percent",
    discoOnAnswered: "percent",
    discoDue: "count",
    discoHeld: "count",
    discoShow: "percent",
    discoRescheduled: "count",
    demoBooked: "count",
    demoOnDisco: "percent",
    demoDue: "count",
    demoHeld: "count",
    demoRescheduled: "count",
    demoShow: "percent",
    closings: "count",
    closeRate: "percent",
    value: "cents",
    averageValue: "cents",
});
/** I numeri per cui meno è meglio: le riprogrammate. La loro freccia in su è arancione. */
export const LOWER_IS_BETTER = new Set(["discoRescheduled", "demoRescheduled"]);
/** Un intero arrotondato a metà lontano da zero (2,5 → 3 e −2,5 → −3), senza lo zero negativo. */
function roundHalfAwayFromZero(value) {
    const rounded = Math.sign(value) * Math.round(Math.abs(value));
    return rounded === 0 ? 0 : rounded;
}
/**
 * `part` su `whole` in percento con un decimale (25,04 → 25,0; 25,05 → 25,1), oppure `null` se `whole` è zero. Si
 * moltiplica prima di dividere: con due interi la metà esatta resta esatta e si arrotonda sempre allo stesso modo.
 */
function percentOf(part, whole) {
    if (whole === 0)
        return null;
    return roundHalfAwayFromZero((part * 1000) / whole) / 10;
}
/** I minuti di un periodo: le chiamate arrotondate una volta sul totale dei secondi, più disco e demo. */
export function reportMinutesOf(metrics) {
    const calls = roundHalfAwayFromZero(metrics.callSeconds / 60);
    return { calls, disco: metrics.discoMinutes, demo: metrics.demoMinutes, total: calls + metrics.discoMinutes + metrics.demoMinutes };
}
/** Percentuali con un decimale (25,04 → 25,0), minuti interi, valori in centesimi; `null` con un denominatore zero. */
export function reportKpiValue(id, metrics) {
    switch (id) {
        case "calls":
        case "answered":
        case "leads":
        case "discoBooked":
        case "discoDue":
        case "discoHeld":
        case "discoRescheduled":
        case "demoBooked":
        case "demoDue":
        case "demoHeld":
        case "demoRescheduled":
        case "closings":
            return metrics[id];
        case "answerRate":
            return percentOf(metrics.answered, metrics.calls);
        case "leadRate":
            return percentOf(metrics.answeredLeads, metrics.leads);
        case "minutes":
            return reportMinutesOf(metrics).total;
        case "discoOnCalls":
            return percentOf(metrics.discoBooked, metrics.calls);
        case "discoOnAnswered":
            return percentOf(metrics.discoBooked, metrics.answered);
        case "discoShow":
            return percentOf(metrics.discoHeld, metrics.discoDue);
        case "demoOnDisco":
            return percentOf(metrics.demoBooked, metrics.discoHeld);
        case "demoShow":
            return percentOf(metrics.demoHeld, metrics.demoDue);
        case "closeRate":
            return percentOf(metrics.closings, metrics.demoHeld);
        case "value":
            return metrics.valueCents;
        case "averageValue":
            return metrics.closings === 0 ? null : roundHalfAwayFromZero(metrics.valueCents / metrics.closings);
        default:
            throw new RangeError(`unknown report KPI: ${String(id)}`);
    }
}
/** Gli obiettivi decisi (SEZ-2): disco su chiamate 8–10%, show up demo almeno 50%. Non si salvano e non si cambiano. */
export const FIXED_REPORT_TARGETS = Object.freeze({
    discoOnCalls: Object.freeze({ min: 8, max: 10 }),
    demoShow: Object.freeze({ min: 50 }),
});
/** Gli altri, dalla colonna «OBIETTIVO» di «Calcolo Sales»: finché non se ne salvano altri. */
export const DEFAULT_EDITABLE_REPORT_TARGETS = Object.freeze({
    discoOnAnswered: Object.freeze({ min: 18, max: 24 }),
    discoShow: Object.freeze({ min: 50, max: 60 }),
    demoOnDisco: Object.freeze({ min: 60, max: 80 }),
    closeRate: Object.freeze({ min: 20 }),
});
/**
 * Gli obiettivi dei colori: i decisi vincono sempre, anche su una riga salvata che li contenesse; gli altri vengono da
 * quelli salvati, oppure, se non ce ne sono, da «Calcolo Sales».
 */
export function reportTargetsOf(stored) {
    const editable = stored ?? DEFAULT_EDITABLE_REPORT_TARGETS;
    return {
        discoOnCalls: FIXED_REPORT_TARGETS.discoOnCalls,
        discoOnAnswered: editable.discoOnAnswered ?? DEFAULT_EDITABLE_REPORT_TARGETS.discoOnAnswered,
        discoShow: editable.discoShow ?? DEFAULT_EDITABLE_REPORT_TARGETS.discoShow,
        demoOnDisco: editable.demoOnDisco ?? DEFAULT_EDITABLE_REPORT_TARGETS.demoOnDisco,
        demoShow: FIXED_REPORT_TARGETS.demoShow,
        closeRate: editable.closeRate ?? DEFAULT_EDITABLE_REPORT_TARGETS.closeRate,
    };
}
/** Il colore: `ok` (verde con ✓) dal minimo in su, `below` (arancione con ↓) sotto; `null` senza numero o senza obiettivo. */
export function reportTargetStatus(value, target) {
    if (value === null || target === undefined)
        return null;
    return value < target.min ? "below" : "ok";
}
/** La parola accanto all'obiettivo: «sotto» sotto il minimo, «sopra» oltre il massimo, se no «in linea». */
export function reportTargetWord(value, target) {
    if (value < target.min)
        return "below";
    return target.max !== undefined && value > target.max ? "above" : "inLine";
}
/** La freccia di un numero fra il periodo e quello prima, oppure `null` se uno dei due non ha il numero (denominatore zero). */
export function reportDelta(id, current, previous) {
    const now = reportKpiValue(id, current);
    const before = reportKpiValue(id, previous);
    if (now === null || before === null)
        return null;
    let kind;
    let change;
    if (REPORT_KPI_UNITS[id] === "percent") {
        // Le percentuali hanno già un decimale: la differenza si fa in decimi interi, senza resti della virgola mobile.
        change = (Math.round(now * 10) - Math.round(before * 10)) / 10;
        kind = "points";
    }
    else if (before === 0) {
        change = now;
        kind = "first";
    }
    else {
        change = roundHalfAwayFromZero(((now - before) * 100) / before);
        kind = "percent";
    }
    if (change === 0)
        return { kind: "stable", change: 0, tone: "flat" };
    const better = change > 0 !== LOWER_IS_BETTER.has(id);
    return { kind, change, tone: better ? "good" : "bad" };
}
//# sourceMappingURL=report-kpis.js.map